import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LocationMarkerIcon, 
  CalendarIcon, 
  CurrencyDollarIcon,
  UserGroupIcon,
  FilterIcon,
  ExclamationIcon,
  PhotographIcon,
  EyeIcon,
  DocumentTextIcon,
  StarIcon,
  ClockIcon,
  TrendingUpIcon
} from '@heroicons/react/outline';
import { calculateDistance, formatLocation } from '../utils/locationUtils';

const RFQCard = ({ rfq, showImage = true, className = "", showActions = true }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isHovered, setIsHovered] = useState(false);

  const getUrgencyBadge = (urgency) => {
    const urgencyMap = {
      'high': { 
        color: 'bg-gradient-to-r from-red-500 to-red-600', 
        textColor: 'text-white',
        icon: 'bg-red-400',
        label: 'High Priority' 
      },
      'medium': { 
        color: 'bg-gradient-to-r from-yellow-500 to-orange-500', 
        textColor: 'text-white',
        icon: 'bg-yellow-400',
        label: 'Medium Priority' 
      },
      'low': { 
        color: 'bg-gradient-to-r from-green-500 to-emerald-500', 
        textColor: 'text-white',
        icon: 'bg-green-400',
        label: 'Low Priority' 
      }
    };
    
    const config = urgencyMap[urgency] || urgencyMap['medium'];
    
    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${config.color} ${config.textColor} shadow-md`}>
        <div className={`w-2 h-2 rounded-full ${config.icon} mr-2 animate-pulse`}></div>
        {config.label}
      </span>
    );
  };

  const formatBudget = (budget) => {
    if (!budget) {
      return 'Budget not specified';
    }
    
    // If budget is a number, format it directly
    if (typeof budget === 'number') {
      return `₦${budget.toLocaleString()}`;
    }
    
    // If budget is an object with min/max properties (range)
    if (typeof budget === 'object' && budget.min !== undefined && budget.max !== undefined) {
      if (budget.min === 0 && budget.max === 0) {
        return 'Budget not specified';
      }
      if (budget.min === budget.max) {
        return `₦${budget.min.toLocaleString()}`;
      }
      return `₦${budget.min.toLocaleString()} - ₦${budget.max.toLocaleString()}`;
    }
    
    // If budget is an object with value/amount properties
    if (typeof budget === 'object') {
      if (budget.value !== undefined) {
        return `₦${Number(budget.value).toLocaleString()}`;
      }
      if (budget.amount !== undefined) {
        return `₦${Number(budget.amount).toLocaleString()}`;
      }
      // If it's a Firestore timestamp or other object, try to convert to string
      return `₦${String(budget)}`;
    }
    
    // If budget is a string, try to convert to number
    if (typeof budget === 'string') {
      const numBudget = parseFloat(budget);
      if (!isNaN(numBudget)) {
        return `₦${numBudget.toLocaleString()}`;
      }
      return budget; // Return as is if it's not a valid number
    }
    
    return 'Budget not specified';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date not specified';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleViewDetails = () => {
    navigate(`/rfq/${rfq.id}`, { state: { rfq } });
  };

  const handleSubmitOffer = () => {
    if (!user) {
      // For unauthenticated users, navigate directly to quote form
      // The form will handle authentication when they try to submit
      navigate(`/submit-quote/${rfq.id}`, { state: { rfq } });
      return;
    }
    
    // User is authenticated, proceed to quote form
    navigate(`/submit-quote/${rfq.id}`, { state: { rfq } });
  };

  // Calculate distance if user has location data
  const getDistanceInfo = () => {
    if (!user?.locationData?.latitude || !rfq.locationData?.latitude) {
      return null;
    }

    const distance = calculateDistance(
      user.locationData.latitude,
      user.locationData.longitude,
      rfq.locationData.latitude,
      rfq.locationData.longitude
    );

    return {
      distance: Math.round(distance),
      isInRange: distance <= (rfq.targetRadius || 100)
    };
  };

  const distanceInfo = getDistanceInfo();

  return (
    <>
      <div 
        className={`group bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Enhanced Card Header with Gradient */}
        <div className="relative overflow-hidden rounded-t-2xl">
          {/* Background Gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-blue-50 opacity-60"></div>
          
          {/* Image Preview */}
          {showImage && rfq.media && rfq.media.length > 0 && (
            <div className="relative">
              <img
                src={typeof rfq.media[0] === 'string' ? rfq.media[0] : rfq.media[0].uri}
                alt="RFQ"
                className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              {rfq.media.length > 1 && (
                <div className="absolute top-3 right-3 bg-black/80 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full flex items-center shadow-lg">
                  <PhotographIcon className="h-3 w-3 mr-1" />
                  +{rfq.media.length - 1}
                </div>
              )}
            </div>
          )}

          {/* Header Content */}
          <div className="relative p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-orange-600 transition-colors duration-300">
                  {rfq.textContent || rfq.title || 'RFQ Title'}
                </h3>
                <div className="flex items-center gap-2 mb-3">
                  {getUrgencyBadge(rfq.urgency)}
                  
                  {/* Distance Badge */}
                  {distanceInfo && (
                    <div className={`px-3 py-1.5 rounded-full text-xs font-semibold shadow-md ${
                      distanceInfo.isInRange 
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' 
                        : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
                    }`}>
                      <LocationMarkerIcon className="h-3 w-3 inline mr-1" />
                      {distanceInfo.distance}km away
                    </div>
                  )}
                </div>
              </div>
              
              {/* Floating Action Indicator */}
              <div className={`transition-all duration-300 ${isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center shadow-lg">
                  <TrendingUpIcon className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            {/* Description */}
            <p className="text-gray-600 text-sm mb-6 line-clamp-3 leading-relaxed">
              {rfq.textContent || rfq.description || 'No description available'}
            </p>
          </div>
        </div>

        {/* Enhanced Key Information */}
        <div className="p-6 space-y-4">
          {/* Budget - Highlighted */}
          <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-4 border border-orange-100">
            <div className="flex items-center text-sm">
              <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center mr-3">
                <CurrencyDollarIcon className="h-4 w-4 text-white" />
              </div>
              <div>
                <div className="text-xs text-gray-500 font-medium">Budget</div>
                <div className="font-bold text-gray-900">
                  {formatBudget(rfq.budget)}
                </div>
              </div>
            </div>
          </div>

          {/* Other Information Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center text-sm text-gray-600 group/item">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3 group-hover/item:bg-blue-200 transition-colors">
                <CalendarIcon className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Posted</div>
                <div className="font-medium">{formatDate(rfq.timestamp || rfq.createdAt)}</div>
              </div>
            </div>

            <div className="flex items-center text-sm text-gray-600 group/item">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3 group-hover/item:bg-green-200 transition-colors">
                <LocationMarkerIcon className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Location</div>
                <div className="font-medium line-clamp-1">
                  {formatLocation(rfq.locationData) || rfq.location || 'Not specified'}
                </div>
              </div>
            </div>

            <div className="flex items-center text-sm text-gray-600 group/item">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3 group-hover/item:bg-purple-200 transition-colors">
                <UserGroupIcon className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Company</div>
                <div className="font-medium line-clamp-1">
                  {rfq.authorName || rfq.userName || 'Anonymous'}
                </div>
              </div>
            </div>

            <div className="flex items-center text-sm text-gray-600 group/item">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mr-3 group-hover/item:bg-indigo-200 transition-colors">
                <FilterIcon className="h-4 w-4 text-indigo-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Category</div>
                <div className="font-medium line-clamp-1">
                  {(rfq.categories || [rfq.channel || rfq.category || 'General']).join(', ')}
                </div>
              </div>
            </div>
          </div>

          {/* Target Radius */}
          {rfq.targetRadius && (
            <div className="flex items-center text-sm text-gray-600 group/item">
              <div className="w-8 h-8 bg-cyan-100 rounded-lg flex items-center justify-center mr-3 group-hover/item:bg-cyan-200 transition-colors">
                <LocationMarkerIcon className="h-4 w-4 text-cyan-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Target Area</div>
                <div className="font-medium">Within {rfq.targetRadius}km radius</div>
              </div>
            </div>
          )}

          {/* Offers/Quotes Count */}
          <div className="flex items-center text-sm text-gray-600 group/item">
            <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center mr-3 group-hover/item:bg-yellow-200 transition-colors">
              <DocumentTextIcon className="h-4 w-4 text-yellow-600" />
            </div>
            <div>
              <div className="text-xs text-gray-500">Responses</div>
              <div className="font-medium">
                {rfq.replyCount || 0} {rfq.id % 2 === 0 ? 'offer' : 'quote'}{(rfq.replyCount || 0) !== 1 ? 's' : ''} received
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Action Buttons */}
        <div className="p-6 pt-0">
          <div className="flex gap-3">
            <button
              onClick={handleViewDetails}
              className="flex-1 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 py-3 px-4 rounded-xl text-sm font-semibold hover:from-gray-200 hover:to-gray-300 transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg transform hover:-translate-y-1"
            >
              <EyeIcon className="h-4 w-4 mr-2" />
              View Details
            </button>
            <button
              onClick={handleSubmitOffer}
              className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-4 rounded-xl text-sm font-semibold hover:from-orange-600 hover:to-red-600 transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg transform hover:-translate-y-1"
            >
              <DocumentTextIcon className="h-4 w-4 mr-2" />
              {rfq.id % 2 === 0 ? 'Submit Offer' : 'Submit Quote'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RFQCard; 